import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, TaptapList } from 'sonata-react-components';

import { groupingMessages } from '../groupingMessages';

import type { GroupingItem } from '../../hooks/useGroupingItems';

export interface GroupMembersListProps {
  members: GroupingItem[];
  onRemove: (ids: string[]) => void;
  /** Lists the members without selection or remove controls (e.g. cartographic groups). Defaults to `false`. */
  readOnly?: boolean;
}

/** Members of the edited group: per-row remove, or "Select all" / row selection + "Delete" (unless read-only). */
export function GroupMembersList({ members, onRemove, readOnly = false }: Readonly<GroupMembersListProps>) {
  const { formatMessage } = useIntl();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const checkedMembers = useMemo(() => members.filter(({ id }) => checkedIds.includes(id)), [members, checkedIds]);

  const remove = (ids: string[]) => {
    onRemove(ids);
    setCheckedIds(current => current.filter(id => !ids.includes(id)));
  };

  return (
    <TaptapList<GroupingItem>
      className="editGroupsModal__members"
      data={members}
      defaultSelectedItems={checkedMembers}
      id="editGroupsModal-members"
      onSelectionChange={selected => setCheckedIds(selected.map(({ id }) => id))}
      selectionType={readOnly ? undefined : 'rowClick'}
    >
      <TaptapList.Toolbar>
        <TaptapList.Toolbar.SelectionSummary />
        {!readOnly && (
          <>
            <TaptapList.Toolbar.SelectAll />
            <ButtonUI
              disabled={checkedIds.length === 0}
              icon="fa-regular fa-trash"
              id="editGroupsModal-deleteMembers"
              onClick={() => remove(checkedIds)}
              shape="link"
              size="ultraSmall"
              text={formatMessage(groupingMessages.deleteMembers)}
              type="secondary"
            />
          </>
        )}
      </TaptapList.Toolbar>
      <TaptapList.RowsList<GroupingItem>>
        {rows =>
          rows.map(member => (
            <TaptapList.RowsList.Row id={member.id} key={member.id}>
              <div className="editGroupsModal__member">
                <span className="editGroupsModal__memberName">{member.name}</span>
                {member.detail && <span className="editGroupsModal__memberDetail">{member.detail}</span>}
              </div>
              {!readOnly && (
                <span data-row-action>
                  <ButtonUI
                    icon="fa-solid fa-xmark"
                    id={`editGroupsModal-remove-${member.id}`}
                    onClick={() => remove([member.id])}
                    shape="link"
                    size="ultraSmall"
                    title={formatMessage(groupingMessages.removeMember, { name: member.name })}
                    type="secondary"
                  />
                </span>
              )}
            </TaptapList.RowsList.Row>
          ))
        }
      </TaptapList.RowsList>
    </TaptapList>
  );
}
